import { Component, EventEmitter, Input, Output } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { DID_STATUS, DID } from 'voxloud-types/dist/models';
import { DidsService } from 'src/app/domains/dids/services';
import { NotificationService } from 'src/app/shared/services/notification/notification.service';

import { Company } from 'src/app/domains/companies/models';
import { ConfirmActivateDidPopupComponent } from '../confirm-activate-did-popup/confirm-activate-did-popup.component';
import { AddVerificationDataPopupComponent } from '../add-verification-data-popup/add-verification-data-popup.component';
import { ActivateTestingDidPopupComponent } from '../activate-testing-did-popup/activate-testing-did-popup.component';
import { DocumentsPopupComponent } from '../documents-popup/documents-popup.component';
import { Paginated } from 'src/app/shared/paginated.model';
import { ReactivateDeletedDidPopupComponent } from '../reactivate-deleted-did-popup/reactivate-deleted-did-popup.component';

// TODO: refactor this component to improve I/O
// part of actions are done inside this component,
// part is done in parent component.
// we need unified approach to handle actions.
// suppose to have 1 output with did + action type that will be delegated to parent
// and 1 output to re-fetch table data
@Component({
  selector: 'gdp-dids-table',
  templateUrl: './dids-table.component.html',
  styleUrls: ['./dids-table.component.scss']
})
export class DidsTableComponent {
  @Input() dids: DID[];
  @Input() loading: boolean;
  @Input() company: Company;
  @Input() planAddonHandling: string;
  @Output() onReplaceWithExistingDid: EventEmitter<DID> = new EventEmitter();
  @Output() onReplaceWithNewDid: EventEmitter<DID> = new EventEmitter();
  @Output() onFetchDidsChanges: EventEmitter<any> = new EventEmitter();
  @Output() onReactivateDid: EventEmitter<any> = new EventEmitter();
  @Output() onUpdateDidProvider: EventEmitter<DID> = new EventEmitter();
  public DID_STATUS = DID_STATUS;
  public updatingWhatsapp: number | null = null;
  public updatingInboundSms: number | null = null;

  constructor(
    private notification: NotificationService,
    private didsService: DidsService,
    private modalService: NgbModal,
    private translate: TranslateService
  ) { }

  public getTooltip(did: DID) {
    if ([null, undefined].includes(did.verification_data_id) && [null, undefined].includes(did.portability_request_id)) {
      return 'company.dids.dids-list.table.tooltip.portability-vd-details-missing';
    } else if (did.is_from_pool) {
      return 'company.dids.dids-list.table.tooltip.vd-details-tmp';
    } else {
      return 'company.dids.dids-list.table.tooltip.portability-vd-details';
    }
  }

  public deleteDid(did: DID) {
    this.notification.showConfirmation('company.dids.dids-list.confirm-delete').then((result: string) => {
      if (result === 'ok') {
        did._isDeleting = true;
        this.getDeleteDidObservable(did).subscribe(() => {
          this.dids = this.dids.filter((el) => el.id !== did.id);
          did._isDeleting = false;
          this.notification.success('company.dids.dids-list.success-delete');
        }, () => {
          did._isDeleting = false;
        });
      }
    });
  }

  public onActivateDid(did: DID) {
    switch (did.status) {
      case DID_STATUS.UNDER_TESTING: {
        this.openActivateTestingDidPopup(did);
      }
        break;
      case DID_STATUS.SCHEDULED_DELETION: {
        this.openReactivateDeletedDidPopup(did);
      }
        break;
      default: {
        this.openActivateConfirmationPopup(did);
      }
        break;
    }
  }

  private openActivateTestingDidPopup(did: DID): void {
    const modal = this.modalService.open(ActivateTestingDidPopupComponent, {
      size: 'lg'
    });
    modal.componentInstance.did = did;
    modal.componentInstance.companyId = this.company.companyId;
    modal.componentInstance.planAddonHandling = this.planAddonHandling;
    modal.componentInstance.onEmitValue.pipe(take(1))
      .subscribe((res: string) => {
        const message = this.translate.instant('company.dids.activate-testing-did.success', { value: res });
        this.notification.success(message);
        this.onFetchDidsChanges.emit();
      });
  }

  private openReactivateDeletedDidPopup(did: DID): void {
    const modal = this.modalService.open(ReactivateDeletedDidPopupComponent, {
      size: 'lg'
    });
    modal.componentInstance.did = did;
    modal.componentInstance.companyId = this.company.companyId;
    modal.componentInstance.planAddonHandling = this.planAddonHandling;
    modal.componentInstance.onReactivateDid
      .pipe(take(1))
      .subscribe((message: string) => {
        this.notification.success(message);
        this.onFetchDidsChanges.emit();
      });
  }

  onAddVDPopup(did: DID) {
    const modal = this.modalService.open(AddVerificationDataPopupComponent, {
      size: 'lg'
    });
    modal.componentInstance.did = did;
    modal.componentInstance.companyId = this.company.companyId;
    modal.componentInstance.onCreateVerificationData.pipe(take(1))
      .subscribe((res: DID) => {
        const message = this.translate.instant('company.did.add-verification-data-popup.success', { value: res.number });
        this.notification.success(message);
        this.onFetchDidsChanges.emit();
      });
  }

  private openActivateConfirmationPopup(did: DID) {
    did._isActivating = true;
    this.didsService
      .getDids(0, 10, { company_id: this.company.companyId, is_from_pool: true })
      .subscribe(
        (res: Paginated<DID>) => {
          did._isActivating = false;
          const modal = this.modalService.open(ConfirmActivateDidPopupComponent);
          modal.componentInstance.hasTemporaryNumber = res.content.length > 0;
          modal.result.then((result: string) => {
            if (result === 'ok') {
              did._isActivating = true;
              this.planAddonHandling === 'manual' ? this.activateDidManually(did) : this.activateDid(did);
            }
          }).catch(() => { });
        }, () => {
          did._isActivating = false;
        }
      );
  }

  private activateDidManually(did: DID): void {
    this.didsService.activateDidManually(did.id, [
      {
        status: 404,
        message: 'company.dids.dids-list.error-activate'
      }
    ])
      .subscribe((did: DID) => {
        this.dids = [...this.dids.map((el) => el.id === did.id ? did : el)];
        this.notification.success('company.dids.dids-list.success-activate');
        did._isActivating = false;
      }, () => {
        did._isActivating = false;
      }
    );
  }

  private activateDid(did: DID): void {
    this.didsService.activateDid(this.company.companyId, did.id, [
      {
        status: 404,
        message: 'company.dids.dids-list.error-activate'
      }
    ])
      .subscribe((r: string) => {
        const updatedDid: DID = {
          ...did,
          number: r,
          status: DID_STATUS.ACTIVE,
          _isActivating: false
        };
        this.dids = [...this.dids.map((el) => el.id === updatedDid.id ? updatedDid : el)];
        this.notification.success('company.dids.dids-list.success-activate');
      }, () => {
        did._isActivating = false;
      }
    );
  }

  public checkDidDeletionStatus(did: DID): boolean {
    return did.status === DID_STATUS.SCHEDULED_DELETION || did.status === DID_STATUS.DELETED;
  }

  public getReplaceTooltipToken(did: DID): string {
    if (did.is_from_pool) {
      return 'company.dids.dids-list.table.tooltip.replace-tmp';
    } else if (this.checkDidDeletionStatus(did)) {
      return 'company.dids.dids-list.table.tooltip.deletion'
    } else {
      'company.dids.dids-list.table.tooltip.replace-exinsting'
    }
  }

  public openReplaceWithExistingDidPopup(did: DID) {
    this.onReplaceWithExistingDid.emit(did);
  }

  public openUpdateDidProviderPopup(did: DID) {
    this.onUpdateDidProvider.emit(did);
  }

  public openReplaceWithNewDidPopup(did: DID) {
    this.onReplaceWithNewDid.emit(did);
  }


  public openDocumentsDetails(did: DID) {
    const modal = this.modalService.open(DocumentsPopupComponent, {
      size: 'lg'
    });
    modal.componentInstance.did = did;
    modal.componentInstance.companyId = this.company.companyId;
  }

  public toggleWhatsappConfirmation(did: DID, value: boolean) {
    this.updatingWhatsapp = did.id;
    this.notification.showConfirmationWithAlphaname(
      value
        ? 'company.dids.dids-list.popup.confirm-whatsapp-toggle.enable'
        : 'company.dids.dids-list.popup.confirm-whatsapp-toggle.disable',
      this.company.alphaname
    ).then((result: string) => {
      if (result === 'ok') {
        this.didsService.toggleWhatsapp(did.id)
          .subscribe({
            next: (response: DID) => {
              this.rewriteDids(this.dids.map((el) => el.id === did.id ? response : el));
              this.updatingWhatsapp = null;
            }, error: () => {
              this.rewriteDids(
                this.dids.map((el) => el.id === did.id ? { ...el, is_whatsapp_enabled: !value } : el)
              );
              this.updatingWhatsapp = null;
            },
          });
      } else {
        this.rewriteDids(
          this.dids.map((el) => el.id === did.id ? { ...el, is_whatsapp_enabled: !value } : el)
        )
        this.updatingWhatsapp = null;
      }
    });
  }

  public toggleInboundSmsConfirmation(did: DID, value: boolean): void {
    this.updatingInboundSms = did.id;
    this.notification.showConfirmationWithAlphaname(
      value
        ? 'company.dids.dids-list.popup.confirm-inbound-sms-toggle.enable'
        : 'company.dids.dids-list.popup.confirm-inbound-sms-toggle.disable',
      this.company.alphaname
    ).then((result: string) => {
      if (result === 'ok') {
        this.didsService.toggleInboundSms(did.id)
          .subscribe({
            next: (response: DID) => {
              this.rewriteDids(this.dids.map((el) => el.id === did.id ? response : el));
              this.updatingInboundSms = null;
            }, error: () => {
              this.rewriteDids(
                this.dids.map((el) => el.id === did.id ? { ...el, is_inbound_sms_enabled: !value } : el)
              );
              this.updatingInboundSms = null;
            },
          });
      } else {
        this.rewriteDids(
          this.dids.map((el) => el.id === did.id ? { ...el, is_inbound_sms_enabled: !value } : el)
        )
        this.updatingInboundSms = null;
      }
    });
  }

  private rewriteDids(dids: DID[]): void {
    this.dids = [...dids];
  }

  private getDeleteDidObservable(did: DID): Observable<any> {
    return this.planAddonHandling === 'manual' ?
      this.didsService.deleteDidManually(did.id, this.getErrorObject()) :
      this.didsService.deleteDid(this.company.companyId, did.id, this.getErrorObject());
  }

  private getErrorObject(): any {
    return [
      {
        status: 404,
        message: 'company.dids.dids-list.error-delete'
      }
    ]
  }

}
